import {Injectable} from '@angular/core';
import {constants, NodeStatus, RefModelStoreService} from '@q9elements/ui-kit/common';
import {defaultTo, endsWith} from 'lodash';
import {INode, ITreeNode} from 'src/app/editor-interface/models/grid-node.interface';
import {IMPACT_CLASSES} from 'src/app/editor-interface/models/node-impact.interface';
import {GdprStatusesService} from 'src/app/editor-interface/services/gpdr-statuses.service';
import {GridContextService} from 'src/app/editor-interface/services/strategies/grid-context.service';
import {SfApiService} from '../../services/sf-api.service';

@Injectable()
export class GridNodeFormatterService {
  private refModel = this.rmeStore.refModel;

  constructor(
    private gdprStatusesService: GdprStatusesService,
    private gridContext: GridContextService,
    private sfApiService: SfApiService,
    private rmeStore: RefModelStoreService
  ) {}

  public formatNode(node: any, isRoot = false): ITreeNode {
    const {_id, childTypes, customId, nodes, owner, sf, tags, identity} = node;
    const isRefModel = Boolean(owner);

    const newNode = {
      childTypes: childTypes || [],
      customId: customId || '',
      description: this.getNodeDescription(node),
      gdprEnabled: this.getNodeGDPREnabled(node),
      gdprStatus: this.getNodeGDPRStatus(node),
      hasChildren: nodes.length > 0,
      id: _id,
      impact: this.getNodeImpactStatus(node),
      impactClass: this.getNodeImpactClass(node),
      isRefModel,
      links: this.getNodeLinks(node),
      name: this.getNodeName(node),
      nodes: nodes,
      optimizeStatus: this.getNodeOptimizeStatus(node),
      parentId: this.gridContext.execute().getParentId(node.parentId, node.refModel),
      parentPath: node.parentPath,
      shouldShowStatus: this.getNodeShouldShowStatus(node),
      status: this.getNodeStatus(node),
      tags: tags || [],
      type: sf?.type || '',
      _id,
      identity
    } as ITreeNode;

    if (isRoot) {
      newNode.parentId = null;
    }

    return newNode;
  }

  private getNodeImpact(node: INode): string {
    const {impactDetails} = node;
    const {totalImpact} = impactDetails;

    return constants.fieldImpactLevels[totalImpact];
  }

  private getNodeImpactStatus(node: INode): string {
    return this.refModel.external && node.impactDetails && !this.getNodeIsDeletedOrProposed(node)
      ? this.getNodeImpact(node)
      : '';
  }

  private getNodeImpactClass(node: INode): string {
    return this.refModel.external && node.impactDetails && !this.getNodeIsDeletedOrProposed(node)
      ? IMPACT_CLASSES[this.getNodeImpact(node)]
      : '';
  }

  private getNodeIsDeletedOrProposed(node: INode) {
    return [NodeStatus.DELETED, NodeStatus.PROPOSED].includes(node.status as any);
  }

  private getNodeGDPREnabled(node: INode): boolean {
    return defaultTo(node.gdpr && node.gdpr.enabled, false);
  }

  private getNodeGDPRStatus(node: INode): string {
    return node?.gdpr?.status ? this.gdprStatusesService.gdprStatuses.get(node.gdpr.status) : '';
  }

  private getNodeOptimizeStatus(node: INode): string {
    if (node.status !== NodeStatus.PROPOSED && node.sf && node.sf.type !== 'Processes') {
      return node.optimize?.optimizeStatus || node.optimizeStatus || '';
    }

    return '';
  }

  private getNodeStatus(node: INode): string {
    return this.refModel.external ? node.status : '';
  }

  private getNodeShouldShowStatus(node: INode): boolean {
    const nodeHasChildren = node.nodes.length;
    const isObjectChild = node.sf && constants.refModelItemsOfObject.includes(node.sf.type);
    const nodeIsCustomObjectType =
      node.parentPath &&
      constants.customObjectNodeTypes.some(type => node.parentPath.endsWith(type));
    const nodeIsStandardObjectType = node.identity?.startsWith('Object.StandardObject.');
    const isStandardValueSet = node.parentPath && node.parentPath.endsWith('StandardValueSet');
    const isGlobalValueSet = node.parentPath && this.isGlobalValueSet(node.parentPath);
    const isPicklistField =
      node.parentPath && node.sf && node.parentPath.endsWith('fields') && nodeHasChildren;
    const isCustomValue = node.sf && node.sf.type === 'Custom Value';
    const isNewOrDeletedCustomValue =
      isCustomValue && node.status !== NodeStatus.UNCHANGED && node.status !== NodeStatus.UPDATED;
    const isDataCloudGroupingNode =
      node.sf && constants.dataCloudGropingNodeTypes.includes(node.sf.type);
    const isDataCloudTypeNode =
      node.parentPath && constants.dataCloudNodeTypes.some(type => node.parentPath.endsWith(type));

    const isAgentforceGroupingNode =
      node.sf && constants.agentforceGroupingNodeTypes.includes(node.sf.type);
    const isAgentforceTypeNode =
      !isAgentforceGroupingNode &&
      node.parentPath &&
      constants.agentforceNodeTypes.some(type => node.parentPath.endsWith(type));

    // some parent types are special and should always display status
    if (
      isDataCloudTypeNode ||
      nodeIsCustomObjectType ||
      (nodeIsStandardObjectType && node.status === NodeStatus.PROPOSED) ||
      isGlobalValueSet ||
      isPicklistField ||
      isAgentforceTypeNode
    ) {
      return true;
    }

    // we currently can't detect changes in CustomValues (individual picklist values)
    // so we're only showing status for New or Deleted or Proposed
    // NOTE: if we update the feature so that we can detect Custom Value changes, we will need to revisit this
    if (isNewOrDeletedCustomValue) {
      return true;
    }

    // all nodes without children get a status
    // EXCEPT object children, StandardValueSets, and any unchanged Custom Values
    // NOTE: if we update the feature so that we can detect Custom Value changes, we will need to revisit this
    return (
      !nodeHasChildren &&
      !isObjectChild &&
      !isCustomValue &&
      !isStandardValueSet &&
      !isDataCloudGroupingNode &&
      !isAgentforceGroupingNode
    );
  }

  private getNodeName(node: INode): string {
    return endsWith(node.parentPath, '.fields')
      ? this.sfApiService.getFieldSuffix(node)
      : node.name;
  }

  private getNodeDescription(node: INode): string {
    return node.external
      ? node.description?.value || ''
      : typeof node.description === 'string'
        ? node.description
        : '';
  }

  private getNodeLinks(node: INode): string | number {
    return !node.links && node.links !== 0 ? '' : node.links;
  }

  private isGlobalValueSet(parentPath: string): boolean {
    const chunks = parentPath.split('.');
    // GlobalValueSets will have a parentPath of
    //    GlobalValueSet
    // or Namespace.*.GlobalValueSet
    if (chunks.length !== 1 && chunks.length !== 3) {
      return false;
    }

    return chunks[chunks.length - 1] === 'GlobalValueSet';
  }
}
